// Client-facing elevation render — colour + design only, no fabrication data.
import { getLaserPreset } from "@/lib/laserCutPresets";

export const DesignVisual = ({
  structureType = "fence", infill = "pickets", colourHex = "#323233",
  heightMm = 1800, totalM = 0, meshApertureMm = 50,
  louvreAngleDeg = 45, bladeOrientation = "vertical", laserCutPreset = "botanical_leaf", laserCutUploadUrl = "",
}) => {
  const W = 360, H = 170, ground = H - 22, top = 28;
  const fenceH = ground - top;
  const posts = [20, 100, 180, 260, 340];
  const isRollTop = structureType === "roll_top";
  const lean = structureType === "safety_barrier" ? -10 : 0;

  const bays = [];
  const patternId = `dv-pat-${laserCutPreset}`;
  for (let b = 0; b < posts.length - 1; b++) {
    const x1 = posts[b] + 5, x2 = posts[b + 1] - 5;
    if (infill === "pickets") {
      for (let x = x1 + 6; x < x2; x += 14) bays.push(<line key={`p${b}-${x}`} x1={x} y1={top + 8} x2={x} y2={ground} stroke={colourHex} strokeWidth={4} />);
    } else if (infill === "slats") {
      for (let y = top + 10; y < ground - 4; y += 13) bays.push(<line key={`s${b}-${y}`} x1={x1} y1={y} x2={x2} y2={y} stroke={colourHex} strokeWidth={7} />);
    } else if (infill === "louvre") {
      // angled overlapping horizontal blades
      for (let y = top + 12; y < ground - 4; y += 14) {
        const cx = (x1 + x2) / 2;
        bays.push(
          <g key={`lv${b}-${y}`} transform={`rotate(${-louvreAngleDeg} ${cx} ${y})`}>
            <rect x={x1} y={y - 4} width={x2 - x1} height={8} fill={colourHex} rx={3} />
          </g>
        );
      }
    } else if (infill === "blade") {
      if (bladeOrientation === "horizontal") {
        for (let y = top + 10; y < ground - 4; y += 10) bays.push(<rect key={`bh${b}-${y}`} x={x1} y={y} width={x2 - x1} height={3.5} fill={colourHex} />);
      } else {
        for (let x = x1 + 4; x < x2; x += 10) bays.push(<rect key={`bv${b}-${x}`} x={x} y={top + 6} width={3} height={fenceH - 6} fill={colourHex} />);
      }
    } else if (infill === "laser_cut") {
      bays.push(<rect key={`lc-bg${b}`} x={x1} y={top + 6} width={x2 - x1} height={fenceH - 8} fill="#f8fafc" stroke={colourHex} strokeWidth={0.7} />);
      bays.push(<rect key={`lc-fill${b}`} x={x1} y={top + 6} width={x2 - x1} height={fenceH - 8} fill={`url(#${patternId})`} />);
    } else if (infill === "mesh") {
      const step = Math.max(7, Math.min(16, meshApertureMm / 6));
      for (let x = x1; x <= x2; x += step) bays.push(<line key={`mv${b}-${x}`} x1={x} y1={top + 6} x2={x} y2={ground} stroke={colourHex} strokeWidth={1} />);
      for (let y = top + 6; y <= ground; y += step) bays.push(<line key={`mh${b}-${y}`} x1={x1} y1={y} x2={x2} y2={y} stroke={colourHex} strokeWidth={1} />);
    } else if (infill === "panels") {
      bays.push(<rect key={`pl${b}`} x={x1} y={top + 6} width={x2 - x1} height={fenceH - 6} fill={colourHex} opacity={0.92} />);
    }
  }

  const preset = infill === "laser_cut" ? getLaserPreset(laserCutPreset) : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-sm bg-gradient-to-b from-sky-50 to-slate-100" data-testid="design-visual">
      {preset && (
        <defs>
          <pattern id={patternId} x="0" y="0" width={30} height={30} patternUnits="userSpaceOnUse">
            {laserCutPreset === "custom" && laserCutUploadUrl ? (
              <image href={laserCutUploadUrl} x="0" y="0" width={30} height={30} preserveAspectRatio="xMidYMid slice" />
            ) : (
              <g transform="scale(0.3)" dangerouslySetInnerHTML={{ __html: preset.render(colourHex) }} />
            )}
          </pattern>
        </defs>
      )}
      <line x1={0} y1={ground + 6} x2={W} y2={ground + 6} stroke="#94a3b8" strokeWidth={2} />
      {bays}
      {posts.map((x) => (
        <g key={x} transform={lean ? `rotate(${lean} ${x} ${ground})` : undefined}>
          <rect x={x - 4} y={top - (lean ? 10 : 0)} width={8} height={ground - top + (lean ? 10 : 0)} fill={colourHex} rx={1} />
        </g>
      ))}
      {!isRollTop && <line x1={12} y1={top + 4} x2={W - 12} y2={top + 4} stroke={colourHex} strokeWidth={6} />}
      {isRollTop && posts.slice(0, -1).map((x, b) => (
        <path key={`rt${b}`} d={`M ${x + 5} ${top + 12} Q ${(x + posts[b + 1]) / 2} ${top - 8} ${posts[b + 1] - 5} ${top + 12}`} fill="none" stroke={colourHex} strokeWidth={4} />
      ))}
      <line x1={12} y1={ground - 2} x2={W - 12} y2={ground - 2} stroke={colourHex} strokeWidth={5} />
      <rect x={4} y={H - 17} width={90} height={14} rx={2} fill="#ffffff" opacity={0.85} />
      <rect x={W - 96} y={H - 17} width={92} height={14} rx={2} fill="#ffffff" opacity={0.85} />
      <text x={10} y={H - 6} fontSize={10.5} fill="#334155" fontFamily="monospace" fontWeight="bold">{`${(totalM || 0).toFixed(1)} m total`}</text>
      <text x={W - 10} y={H - 6} fontSize={10.5} fill="#334155" fontFamily="monospace" fontWeight="bold" textAnchor="end">{`${heightMm} mm high`}</text>
    </svg>
  );
};

export default DesignVisual;
